import { Avatar, Button, Flex, List, Popconfirm, PopconfirmProps, Space, Tag, Tooltip } from "antd";
import { observer } from "mobx-react";
import React, { MouseEvent } from "react";
import { Bar, BarChart, XAxis, YAxis } from "recharts";
import Subjects from "../../models/subjects";
import StudentItemStore, { StudentTicketStore } from "../../stores/students/student-item";

const StudentTicketItem: React.FC<{ student: StudentItemStore, subject: Subjects, item: StudentTicketStore, indx: number }> = ({ student, subject, item, indx }) => {

    const onConfirm: PopconfirmProps['onConfirm'] = (e) => {
        student.unassign(item.data.id);
    };

    const backgroundColor = (indx % 2) ? "#f0f0f0" : "";

    return <List.Item key={item.data.id} style={{backgroundColor: backgroundColor}}
        extra={
            <Flex align="flex-start" gap="small" vertical>
                <Button type='link' href={`/${subject}/students/${student.data.id}/tickets/${item.data.ticketId}`}>Редактировать ответы</Button>
                <Popconfirm
                    title="Удалить проверку"
                    description="Вы уверены, что хотите удалить проверку? Отменить это действие будет невозможно"
                    onConfirm={onConfirm}
                    okText="Да"
                    cancelText="Нет"
                >
                    <Button type='link' danger>Удалить проверку</Button>
                </Popconfirm>
            </Flex>
        }
    >
        <List.Item.Meta
            title={`Вариант ${item.data.ticketId}. Проверен ${new Date(item.data.completeDate).toLocaleString()}. Баллов ${item.data.pointsSum}`}
        />
    </List.Item>
}

const StudentItem: React.FC<{ item: StudentItemStore, indx: number, subject: Subjects }> = ({ item, indx, subject }) => {

    const ticketTimeDesc = item.data.lastTicketDate ? `, последний проверен ${new Date(item.data.lastTicketDate).toLocaleString()}` : "";
    const fullDescription = `Выполнено ${item.data.completedTickets || 0} вариантов ЕГЭ${ticketTimeDesc}. Средний балл ${item.data.avgPoints || 0}`;
    const avatarColor = '#8923fdff';

    const handleShowStatistic = (event: MouseEvent<HTMLAnchorElement>) => {
        item.toggleStatistic();
    }

    const handleCompletedTicket = (event: MouseEvent<HTMLAnchorElement>) => {
        item.loadTicketList();
    }

    return (
        <List.Item key={item.data.id}
            extra={
                <Flex align="flex-start" gap="small" vertical>
                    <Button type="link" onClick={handleShowStatistic}>{item.expandedStatistic ? "Свернуть статистику" : "Посмотреть статистику"}</Button>
                    <Button type="link" onClick={handleCompletedTicket}>{item.expandedTicketList ? "Свернуть назначенные варианты" : "Посмотреть назначенные варианты"}</Button>
                </Flex>
            }>
            <List.Item.Meta
                avatar={<Avatar style={{ backgroundColor: avatarColor, verticalAlign: 'middle' }} size="large" >{item.getShortName()}</Avatar>}
                title={
                    <Space>
                        <b>{item.data.name}</b>
                        {item.data.currentTicket &&
                            <Tag color="purple">Есть непроверенные</Tag>
                        }
                    </Space>
                }
                description={fullDescription}
            />
            {item.expandedStatistic &&
                <Flex align="start" vertical>
                    <BarChart width={1000} height={400} data={item.statistic}>
                        <XAxis type="category" dataKey="number" label={{ value: 'Номер в КИМ', position: 'insideBottom', dx: 0, dy: 5 }} />
                        <YAxis type="number" dataKey="percent" domain={[0, 100]} allowDataOverflow label={{ value: 'Процент выполнения', position: 'insideLeft', dx: 0, dy: 20, angle: -90 }} />
                        <Tooltip />
                        <Bar dataKey="percent" fill="#00aeffff" activeBar={true} isAnimationActive={true} />
                    </BarChart>
                </Flex>
            }
            {item.expandedTicketList &&
                <div>
                    {(item.notFinishedTickets && item.notFinishedTickets.length !== 0) &&
                        <List
                            header={<Tag color="purple">Проверка не закончена</Tag>}
                            bordered
                            itemLayout="vertical"
                            dataSource={item.notFinishedTickets}
                            renderItem={(i, indx) => (
                                <StudentTicketItem {...{ student: item, subject: subject, item: i, indx: indx }} />
                            )}
                            style={{ marginBottom: "10px" }}
                        >
                        </List>
                    }
                    {(item.egeTickets && item.egeTickets.length !== 0) &&
                        <List
                            header={<Tag color="magenta">ЕГЭ</Tag>}
                            bordered
                            itemLayout="vertical"
                            dataSource={item.egeTickets}
                            renderItem={(i, indx) => (
                                <StudentTicketItem {...{ student: item, subject: subject, item: i, indx: indx }} />
                            )}
                            style={{ marginBottom: "10px" }}
                        >
                        </List>
                    }
                    {(item.thematicTickets && item.thematicTickets.length !== 0) &&
                        <List
                            header={<Tag color="cyan">Тематические</Tag>}
                            bordered
                            itemLayout="vertical"
                            dataSource={item.thematicTickets}
                            renderItem={(i, indx) => (
                                <StudentTicketItem {...{ student: item, subject: subject, item: i, indx: indx }} />
                            )}
                        >
                        </List>
                    }
                </div>
            }
        </List.Item>
    );
}

export default observer(StudentItem);